/**
 * Persistent Logger - Logs to console AND localStorage
 * Logs survive page refreshes so you can see what happened
 */

interface LogEntry {
  timestamp: string;
  level: 'log' | 'warn' | 'error' | 'info';
  category: string;
  message: string;
  data?: any;
}

const MAX_LOGS = 200; // Keep last 200 logs
const STORAGE_KEY = 'app-logs';

class PersistentLogger {
  private logs: LogEntry[] = [];

  constructor() {
    // Load existing logs on init
    this.loadLogs();
    
    // Save logs before page unload
    window.addEventListener('beforeunload', () => {
      this.saveLogs();
    });
    
    // Also save periodically
    setInterval(() => {
      this.saveLogs();
    }, 1000);
  }

  private loadLogs(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.logs = JSON.parse(stored);
        console.log(`[PersistentLogger] Loaded ${this.logs.length} previous logs`);
      }
    } catch (error) {
      console.error('[PersistentLogger] Error loading logs:', error);
    }
  }

  private saveLogs(): void {
    try {
      // Keep only last MAX_LOGS entries
      if (this.logs.length > MAX_LOGS) {
        this.logs = this.logs.slice(-MAX_LOGS);
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.logs));
    } catch (error) {
      console.error('[PersistentLogger] Error saving logs:', error);
    }
  }

  private addLog(level: LogEntry['level'], category: string, message: string, data?: any): void {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data
    };
    
    this.logs.push(entry);
    
    // Also log to console with styling
    const style = level === 'error' ? 'color: red' : level === 'warn' ? 'color: orange' : 'color: blue';
    const prefix = `[${entry.timestamp}] [${category}]`;
    console.log(`%c${prefix} ${message}`, style, data || '');
    
    // Save logs
    this.saveLogs();
  }

  log(category: string, message: string, data?: any): void {
    this.addLog('log', category, message, data);
  }

  info(category: string, message: string, data?: any): void {
    this.addLog('info', category, message, data);
  }

  warn(category: string, message: string, data?: any): void {
    this.addLog('warn', category, message, data);
  }

  error(category: string, message: string, data?: any): void {
    this.addLog('error', category, message, data);
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
    localStorage.removeItem(STORAGE_KEY);
    console.log('[PersistentLogger] Logs cleared');
  }

  printLogs(): void {
    console.group('📋 Persistent Logs (Last 50)');
    const recent = this.logs.slice(-50);
    recent.forEach(entry => {
      const style = entry.level === 'error' ? 'color: red' : entry.level === 'warn' ? 'color: orange' : 'color: blue';
      console.log(`%c[${entry.timestamp}] [${entry.category}] ${entry.message}`, style, entry.data || '');
    });
    console.groupEnd();
  }
}

export const persistentLogger = new PersistentLogger();

// Make it available globally for debugging
(window as any).persistentLogger = persistentLogger;
(window as any).showLogs = () => persistentLogger.printLogs();
(window as any).clearLogs = () => persistentLogger.clearLogs();

